import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import LogoAtivaJa from "@/components/LogoAtivaJa";
import { toast } from "sonner";
import {
  ArrowRight, Play, Package, CalendarCheck, Share2, Wrench, CheckCircle2,
  BarChart3, Building2, Factory, HardHat, Zap, Route, ShieldCheck, MapPin, Menu, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TELAS_REAIS = [
  { src: "/landing/screens/empresa.png", perfil: "Empresa", titulo: "app.montaja — Painel da Empresa", d: "Dashboard operacional: serviços, capacidade regional e faturamento." },
  { src: "/landing/screens/montadora.png", perfil: "Montadora", titulo: "app.montaja — Painel da Montadora", d: "Gestão da rede: montadores, regiões atendidas e repasses." },
  { src: "/landing/screens/montador.png", perfil: "Montador", titulo: "app.montaja — Painel do Montador", d: "Oportunidades, agenda, score pessoal e recebimentos." },
  { src: "/landing/screens/cliente.png", perfil: "Cliente", titulo: "app.montaja — Portal do Cliente", d: "Acompanhamento em tempo real, agendamento e chat registrado." },
];

function PlataformaCarousel() {
  const [idx, setIdx] = useState(0);
  const [pausado, setPausado] = useState(false);
  useEffect(() => {
    if (pausado) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % TELAS_REAIS.length), 5000);
    return () => clearInterval(t);
  }, [pausado]);
  const atual = TELAS_REAIS[idx];
  return (
    <div className="mt-12" data-reveal data-testid="dashboard-real"
      onMouseEnter={() => setPausado(true)} onMouseLeave={() => setPausado(false)}>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="telas-reais-tabs">
        {TELAS_REAIS.map((t, i) => (
          <button key={t.perfil} onClick={() => setIdx(i)}
            className={`px-4 py-2 rounded-full text-xs font-bold tracking-wide transition-all duration-300 ${
              i === idx ? "bg-[#FF6700] text-white shadow-[0_8px_24px_-8px_rgba(255,103,0,.7)]"
                : "bg-[#142766]/50 text-[#9AA6C4] border border-[#126BFF]/20 hover:border-[#FF6700]/50"}`}
            data-testid={`tela-tab-${t.perfil.toLowerCase()}`}>
            {t.perfil}
          </button>
        ))}
      </div>
      <div className="rounded-2xl overflow-hidden border border-[#126BFF]/25 shadow-[0_40px_90px_-40px_rgba(18,107,255,.45)]">
        <div className="bg-[#142766] px-4 py-2.5 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#FF6700]" /><span className="w-3 h-3 rounded-full bg-[#126BFF]" /><span className="w-3 h-3 rounded-full bg-white/25" />
          <span className="ml-3 text-[11px] font-bold text-[#B8C4E0] tracking-wide" data-testid="tela-titulo">{atual.titulo}</span>
          <span className="ml-auto hidden sm:block text-[11px] text-[#6FA6FF]">{atual.d}</span>
        </div>
        <div className="relative aspect-[1600/1000] bg-[#0B1533]">
          {TELAS_REAIS.map((t, i) => (
            <img key={t.src} src={t.src} alt={`Tela real do MontaJá — ${t.perfil}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === idx ? 1 : 0 }}
              loading={i === 0 ? "eager" : "lazy"} />
          ))}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#142766]">
            <div key={idx} className="h-full bg-[#FF6700]"
              style={{ animation: pausado ? "none" : "mj-progresso 5s linear forwards" }} />
          </div>
        </div>
      </div>
      <style>{`@keyframes mj-progresso { from { width: 0 } to { width: 100% } }`}</style>
    </div>
  );
}

/* Hook: revela elementos ao entrar na tela */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("mj-in")),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const JORNADA = [
  { icon: Package, t: "Pedido", d: "A empresa importa ou cadastra a OS em segundos." },
  { icon: Share2, t: "Distribuição", d: "Oferta automática aos profissionais elegíveis da região." },
  { icon: CalendarCheck, t: "Agendamento", d: "Cliente confirma a data pelo WhatsApp, sem ligações." },
  { icon: Wrench, t: "Execução", d: "Check-in, checklist e fotos direto do local." },
  { icon: CheckCircle2, t: "Conclusão", d: "Assinatura digital e evidências arquivadas." },
  { icon: BarChart3, t: "Dados", d: "Indicadores, score e financeiro em tempo real." },
];

const PERFIS = [
  { icon: Building2, t: "Empresas", d: "Importe pedidos, acompanhe cada etapa e tenha controle total da operação com evidências e financeiro integrados.", to: "/cadastro-empresa", cta: "Cadastrar empresa" },
  { icon: Factory, t: "Montadoras", d: "Atenda várias empresas, gerencie sua rede por região e cresça com score transparente e distribuição justa.", to: "/cadastro-montadora", cta: "Cadastrar montadora" },
  { icon: HardHat, t: "Montadores", d: "Receba ofertas perto de você, monte sua agenda e receba com extrato claro. Sem exclusividade.", to: "/cadastro-montador", cta: "Cadastrar montador" },
];

const DIFERENCIAIS = [
  { icon: Zap, t: "Automação", d: "Distribuição, reoferta, lembretes e cobranças acontecem sozinhos." },
  { icon: Route, t: "Rastreabilidade", d: "Cada OS com trilha completa: quem recebeu, aceitou, agendou e concluiu." },
  { icon: ShieldCheck, t: "Segurança", d: "Evidências, assinaturas e auditoria jurídica em cada etapa." },
  { icon: MapPin, t: "Cobertura", d: "Distribuição por raio e região, com capacidade controlada por semana." },
];

const PROVAS = [
  { n: 6, suf: "", t: "etapas rastreadas por OS" },
  { n: 5, suf: "", t: "perfis conectados na mesma operação" },
  { n: 24, suf: "h", t: "para reoferta automática sem aceite" },
  { n: 100, suf: "%", t: "das evidências digitais e auditáveis" },
];

const DEPOIMENTOS = [
  { nome: "Renata C.", papel: "Gestora de operações · Loja de móveis", texto: "Antes eu passava o dia ao telefone atrás de montador. Hoje importo os pedidos e acompanho tudo pelo painel — com foto e assinatura no final.", estrelas: 5 },
  { nome: "Carlos M.", papel: "Montadora parceira", texto: "A distribuição por região é justa e o score é transparente. Minha equipe recebe as OS certas, na cidade certa, sem briga por serviço.", estrelas: 5 },
  { nome: "Juliana A.", papel: "Cliente final", texto: "Confirmei a data pelo WhatsApp e acompanhei a montagem em tempo real. O montador chegou no horário e ainda recebi as fotos de tudo pronto.", estrelas: 5 },
];

function Counter({ n, suf }) {
  const ref = useRef(null);
  const [v, setV] = useState(0);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const dur = 900;
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min((t - t0) / dur, 1);
        setV(Math.round(n * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [n]);
  return <span ref={ref}>{v}{suf}</span>;
}

export default function Landing() {
  useReveal();
  const heroRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lead, setLead] = useState({ nome: "", empresa: "", whatsapp: "" });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [usaVideo, setUsaVideo] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    const reduzido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setUsaVideo(desktop && !reduzido);
  }, []);

  /* Paralaxe leve do 3D com o mouse (desativada com prefers-reduced-motion) */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const onMove = (e) => {
      const el = heroRef.current;
      if (!el) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 14;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const enviarLead = async (e) => {
    e.preventDefault();
    if (lead.nome.trim().length < 2 || lead.whatsapp.trim().length < 8) {
      toast.error("Informe seu nome e WhatsApp", { position: "top-center" }); return;
    }
    setEnviando(true);
    try {
      await axios.post(`${API}/leads-demonstracao`, { ...lead, perfil: "demonstracao" });
      setEnviado(true);
    } catch { toast.error("Não foi possível enviar. Tente novamente.", { position: "bottom-center" }); }
    setEnviando(false);
  };

  const navLinks = [
    ["jornada", "Como funciona"], ["plataforma", "Plataforma"], ["perfis", "Perfis"], ["diferenciais", "Diferenciais"],
  ];

  return (
    <div className="bg-[#0B1020] text-white min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="landing-page">
      <style>{`
        [data-reveal]{opacity:0;transform:translateY(22px);transition:opacity .6s ease,transform .6s ease}
        [data-reveal].mj-in{opacity:1;transform:none}
        @keyframes mjFloat{0%,100%{transform:translateY(-8px)}50%{transform:translateY(8px)}}
        .mj-float{animation:mjFloat 7s ease-in-out infinite}
        .mj-glow-btn{box-shadow:0 0 0 rgba(255,103,0,0)}
        .mj-glow-btn:hover{box-shadow:0 0 22px rgba(255,103,0,.45);transform:translateY(-1px) scale(1.02)}
        @keyframes mjLine{from{width:0}to{width:100%}}
        .mj-in .mj-line{animation:mjLine 1.4s ease forwards}
        @keyframes mjFlow{from{transform:translateX(-100%)}to{transform:translateX(0)}}
        .mj-flow{width:max-content;animation:mjFlow 32s linear infinite}
        .mj-flow img{max-width:none;width:56rem}
        @media (prefers-reduced-motion: reduce){
          [data-reveal]{opacity:1;transform:none;transition:none}
          .mj-float{animation:none}
          .mj-flow{animation:none}
        }
      `}</style>

      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1020]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo"><LogoAtivaJa size="sm" inverse /></Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold text-[#B8C4E0]">
            {navLinks.map(([id, l]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-white transition-colors" data-testid={`nav-${id}`}>{l}</button>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/cliente" className="px-4 py-2 rounded-lg text-sm font-bold text-[#B8C4E0] hover:text-white transition-colors" data-testid="landing-area-cliente">Área do Cliente</Link>
            <Link to="/login" className="px-4 py-2 rounded-lg text-sm font-bold border border-white/15 hover:bg-white/5 transition" data-testid="landing-entrar">Entrar</Link>
            <button onClick={() => scrollTo("demonstracao")}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-[#FF6700] transition-all duration-300 mj-glow-btn" data-testid="landing-cta-demo-header">
              Solicitar demonstração
            </button>
          </div>
          <button className="lg:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="landing-menu-toggle">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-[#0B1020]/95 backdrop-blur-xl border-t border-white/5 px-5 py-4 space-y-3" data-testid="landing-menu-mobile">
            {navLinks.map(([id, l]) => (
              <button key={id} onClick={() => { setMenuOpen(false); scrollTo(id); }} className="block w-full text-left text-sm font-semibold text-[#B8C4E0]" data-testid={`nav-mobile-${id}`}>{l}</button>
            ))}
            <div className="flex gap-3 pt-2">
              <Link to="/cliente" className="flex-1 text-center px-4 py-2 rounded-lg text-sm font-bold border border-white/15 text-[#B8C4E0]" data-testid="landing-area-cliente-mobile">Área do Cliente</Link>
              <Link to="/login" className="flex-1 text-center px-4 py-2 rounded-lg text-sm font-bold border border-white/15" data-testid="landing-entrar-mobile">Entrar</Link>
              <button onClick={() => { setMenuOpen(false); scrollTo("demonstracao"); }} className="flex-1 px-4 py-2 rounded-lg text-sm font-bold bg-[#FF6700]" data-testid="landing-cta-demo-mobile">Demonstração</button>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(600px 400px at 75% 30%, rgba(18,107,255,.14), transparent 70%), radial-gradient(500px 350px at 15% 85%, rgba(20,39,102,.5), transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#FF6700] mb-5" data-reveal>Plataforma operacional de montagem</div>
            <h1 className="font-extrabold tracking-tighter leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-reveal data-testid="hero-headline">
              A operação de montagem<br />virou <span className="text-[#FF6700]">plataforma.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#9AA6C4] max-w-md leading-relaxed" data-reveal>
              Do pedido à conclusão, empresas, clientes e profissionais conectados em tempo real.
            </p>
            <div className="mt-9 flex flex-wrap gap-4" data-reveal>
              <button onClick={() => scrollTo("jornada")}
                className="px-7 py-3.5 rounded-xl font-bold bg-[#FF6700] transition-all duration-300 mj-glow-btn inline-flex items-center gap-2" data-testid="hero-cta-conhecer">
                Conhecer o MontaJá <ArrowRight size={17} />
              </button>
              <button onClick={() => scrollTo("plataforma")}
                className="px-7 py-3.5 rounded-xl font-bold border border-[#126BFF] text-[#6FA6FF] hover:bg-[#126BFF]/10 transition inline-flex items-center gap-2" data-testid="hero-cta-operacao">
                <Play size={16} /> Ver operação
              </button>
            </div>
            <div className="mt-10 text-[11px] font-bold uppercase tracking-[0.25em] text-[#5B6A94]" data-reveal>É só montar. MontaJá!</div>
          </div>
          <div className="relative" data-reveal>
            <div ref={heroRef} className="transition-transform duration-200 ease-out will-change-transform">
              {usaVideo ? (
                <video autoPlay muted loop playsInline poster="/landing/hero-3d.jpg"
                  className="rounded-3xl border border-[#126BFF]/20 shadow-2xl w-full" data-testid="hero-video">
                  <source src="/landing/plataforma.webm" type="video/webm" />
                </video>
              ) : (
                <img src="/landing/hero-3d-v2.jpg" alt="Ecossistema MontaJá — módulos de móveis conectados por fluxo digital"
                  className="rounded-3xl border border-[#126BFF]/20 shadow-2xl mj-float w-full" data-testid="hero-3d" loading="eager" />
              )}
            </div>
            <div className="absolute -bottom-5 left-6 sm:left-10 bg-white text-[#0B1020] rounded-2xl px-5 py-3.5 flex items-center gap-3 shadow-2xl">
              <span className="w-9 h-9 rounded-full bg-[#126BFF]/10 flex items-center justify-center"><CheckCircle2 size={19} className="text-[#126BFF]" /></span>
              <div className="leading-tight">
                <div className="text-[11px] text-[#64748B]">Montagem confirmada</div>
                <div className="text-sm font-extrabold">Operação atualizada</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JORNADA */}
      <section id="jornada" className="py-20 sm:py-28 relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#126BFF] mb-3" data-reveal>Como funciona</div>
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl lg:text-5xl max-w-2xl" data-reveal>
            Do pedido à conclusão, <span className="text-[#FF6700]">tudo no mesmo fluxo.</span>
          </h2>
          <div className="mt-6 h-[3px] bg-white/5 rounded-full overflow-hidden max-w-3xl" data-reveal>
            <div className="mj-line h-full bg-gradient-to-r from-[#126BFF] to-[#FF6700] rounded-full" style={{ width: 0 }} />
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {JORNADA.map((e, i) => (
              <div key={e.t} data-reveal style={{ transitionDelay: `${i * 70}ms` }}
                className="rounded-2xl p-6 bg-[#142766]/40 border border-[#126BFF]/15 hover:border-[#126BFF]/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`jornada-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#126BFF]/15 flex items-center justify-center"><e.icon size={19} className="text-[#6FA6FF]" /></span>
                  <span className="text-[11px] font-extrabold text-[#FF6700]">0{i + 1}</span>
                </div>
                <div className="mt-4 font-extrabold text-lg">{e.t}</div>
                <p className="mt-1.5 text-sm text-[#9AA6C4] leading-relaxed">{e.d}</p>
              </div>
            ))}
          </div>
          <div data-reveal className="mt-12 rounded-2xl border border-white/5 w-full max-w-4xl mx-auto overflow-hidden mj-flow-wrap" data-testid="jornada-flow">
            <div className="mj-flow flex">
              <img src="/landing/jornada-3d.jpg" alt="Jornada operacional MontaJá"
                className="w-full flex-shrink-0 opacity-90" loading="lazy" />
              <img src="/landing/jornada-3d.jpg" alt="" aria-hidden="true"
                className="w-full flex-shrink-0 opacity-90" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* PLATAFORMA / DASHBOARD REAL */}
      <section id="plataforma" className="pt-20 sm:pt-28 pb-8 sm:pb-12 relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(700px 400px at 50% 40%, rgba(18,107,255,.10), transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#126BFF] mb-3" data-reveal>A plataforma de verdade</div>
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl lg:text-5xl max-w-2xl" data-reveal>
            Sua operação atualizada <span className="text-[#FF6700]">em tempo real.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#9AA6C4] max-w-xl" data-reveal>
            Estas são as telas reais do MontaJá — o painel da empresa, da montadora, do montador
            e o portal do cliente, exatamente como você vai usar.
          </p>
          <PlataformaCarousel />
        </div>
      </section>

      {/* PERFIS */}
      <section id="perfis" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#126BFF] mb-3" data-reveal>Para quem é</div>
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl lg:text-5xl max-w-2xl" data-reveal>
            Uma rede. <span className="text-[#FF6700]">Todos ganham.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PERFIS.map((p, i) => (
              <div key={p.t} data-reveal style={{ transitionDelay: `${i * 90}ms` }}
                className="rounded-2xl p-7 bg-[#142766]/40 border border-[#126BFF]/15 hover:border-[#FF6700]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                data-testid={`perfil-${p.t.toLowerCase()}`}>
                <span className="w-12 h-12 rounded-xl bg-[#FF6700]/15 flex items-center justify-center"><p.icon size={22} className="text-[#FF6700]" /></span>
                <div className="mt-5 font-extrabold text-xl">{p.t}</div>
                <p className="mt-2 text-sm text-[#9AA6C4] leading-relaxed flex-1">{p.d}</p>
                <Link to={p.to} className="mt-6 inline-flex items-center gap-2 font-bold text-[#FF6700] hover:gap-3 transition-all" data-testid={`cta-${p.to.slice(1)}`}>
                  {p.cta} <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          {/* PARA O CLIENTE */}
          <div className="mt-10 rounded-2xl p-7 sm:p-9 bg-gradient-to-r from-[#142766]/60 to-[#126BFF]/15 border border-[#126BFF]/25"
            data-reveal data-testid="secao-cliente">
            <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#FF6700] mb-2">Para o cliente</div>
                <div className="font-extrabold text-xl md:text-2xl">Comprou um móvel? A montagem é com a gente.</div>
                <p className="mt-2 text-sm text-[#9AA6C4] max-w-xl leading-relaxed">
                  Encontre um montador qualificado perto de você ou acompanhe a montagem do seu pedido de loja em tempo real —
                  agendamento pelo WhatsApp, evidências e assinatura digital.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <Link to="/montadores"
                  className="px-6 py-3 rounded-xl font-bold bg-[#FF6700] text-center transition-all duration-300 mj-glow-btn inline-flex items-center justify-center gap-2"
                  data-testid="cta-cliente-buscar">
                  Procurar montador <ArrowRight size={16} />
                </Link>
                <Link to="/qrcode"
                  className="px-6 py-3 rounded-xl font-bold border border-[#126BFF] text-[#6FA6FF] text-center hover:bg-[#126BFF]/10 transition inline-flex items-center justify-center gap-2"
                  data-testid="cta-cliente-acompanhar">
                  Acompanhar minha montagem
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section id="diferenciais" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#126BFF] mb-3" data-reveal>Diferenciais</div>
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl lg:text-5xl max-w-2xl" data-reveal>
            Mais controle. <span className="text-[#FF6700]">Menos retrabalho.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DIFERENCIAIS.map((d, i) => (
              <div key={d.t} data-reveal style={{ transitionDelay: `${i * 70}ms` }} data-testid={`diferencial-${i + 1}`}
                className="rounded-2xl p-6 bg-white/[.03] border border-white/5 hover:bg-[#142766]/40 transition-colors duration-300">
                <d.icon size={22} className="text-[#6FA6FF]" />
                <div className="mt-4 font-extrabold">{d.t}</div>
                <p className="mt-1.5 text-sm text-[#9AA6C4] leading-relaxed">{d.d}</p>
              </div>
            ))}
          </div>
          {/* PROVA OPERACIONAL */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 rounded-2xl p-8 bg-[#142766]/40 border border-[#126BFF]/15" data-reveal data-testid="prova-operacional">
            {PROVAS.map((p) => (
              <div key={p.t}>
                <div className="font-extrabold text-3xl md:text-4xl text-[#FF6700]"><Counter n={p.n} suf={p.suf} /></div>
                <div className="mt-1 text-xs text-[#9AA6C4] leading-snug">{p.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section id="depoimentos" className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#126BFF] mb-3" data-reveal>Quem usa, confia</div>
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl max-w-2xl" data-reveal>
            Agende, acompanhe e <span className="text-[#FF6700]">comprove.</span>
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="depoimentos">
            {DEPOIMENTOS.map((dp, i) => (
              <div key={dp.nome} data-reveal style={{ transitionDelay: `${i * 90}ms` }}
                className="rounded-2xl p-7 bg-white/[.03] border border-white/5 hover:border-[#126BFF]/30 transition-colors duration-300 flex flex-col"
                data-testid={`depoimento-${i + 1}`}>
                <div className="text-[#FF6700] tracking-widest text-sm" aria-label={`${dp.estrelas} estrelas`}>
                  {"★".repeat(dp.estrelas)}
                </div>
                <p className="mt-4 text-sm text-[#C6CFE6] leading-relaxed flex-1">"{dp.texto}"</p>
                <div className="mt-5 pt-4 border-t border-white/5">
                  <div className="font-extrabold text-sm">{dp.nome}</div>
                  <div className="text-xs text-[#9AA6C4]">{dp.papel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMONSTRAÇÃO */}
      <section id="demonstracao" className="pt-8 sm:pt-12 pb-20 sm:pb-28 relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(600px 350px at 50% 60%, rgba(255,103,0,.08), transparent 70%)" }} />
        <div className="max-w-xl mx-auto px-5 sm:px-8 text-center relative">
          <h2 className="font-extrabold tracking-tighter text-3xl md:text-4xl" data-reveal>Veja o MontaJá funcionando na sua operação.</h2>
          <p className="mt-3 text-[#9AA6C4]" data-reveal>Deixe seu contato e nossa equipe chama você no WhatsApp.</p>
          {enviado ? (
            <div className="mt-8 rounded-2xl bg-[#142766]/50 border border-[#126BFF]/20 p-8" data-testid="lead-enviado">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#FF6700]" />
              <div className="mt-3 font-extrabold text-lg">Recebido! Vamos falar com você em breve.</div>
              <div className="mt-1 text-sm text-[#9AA6C4]">É só montar. MontaJá!</div>
            </div>
          ) : (
            <form onSubmit={enviarLead} className="mt-8 space-y-3 text-left" data-reveal data-testid="lead-form">
              <input value={lead.nome} onChange={(e) => setLead(l => ({ ...l, nome: e.target.value }))} placeholder="Seu nome"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#126BFF] outline-none placeholder:text-[#5B6A94]" data-testid="lead-nome" />
              <input value={lead.empresa} onChange={(e) => setLead(l => ({ ...l, empresa: e.target.value }))} placeholder="Empresa (opcional)"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#126BFF] outline-none placeholder:text-[#5B6A94]" data-testid="lead-empresa" />
              <input value={lead.whatsapp} onChange={(e) => setLead(l => ({ ...l, whatsapp: e.target.value }))} placeholder="WhatsApp com DDD"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#126BFF] outline-none placeholder:text-[#5B6A94]" data-testid="lead-whatsapp" />
              <button type="submit" disabled={enviando} className="w-full py-3.5 rounded-xl font-bold bg-[#FF6700] transition-all duration-300 mj-glow-btn" data-testid="lead-enviar">
                {enviando ? "Enviando..." : "Solicitar demonstração"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <LogoAtivaJa size="md" inverse slogan />
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-[#9AA6C4]">
            <Link to="/montadores" className="hover:text-white transition">Procurar montador</Link>
            <Link to="/qrcode" className="hover:text-white transition">Acompanhar montagem</Link>
            <Link to="/cadastro-empresa" className="hover:text-white transition">Sou empresa</Link>
            <Link to="/cadastro-montadora" className="hover:text-white transition">Sou montadora</Link>
            <Link to="/cadastro-montador" className="hover:text-white transition">Sou montador</Link>
            <Link to="/cliente" className="hover:text-white transition">Área do Cliente</Link>
            <Link to="/login" className="hover:text-white transition">Entrar</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 text-xs text-[#5B6A94]">
          © {new Date().getFullYear()} MontaJá — Tecnologia em movimento. Operação sob controle.
        </div>
      </footer>
    </div>
  );
}
